// Types
import { MeasurementType } from '../../../../../types/global';
import { EquipmentUnitDataVSICrusher } from '../../../../../types/crusher';

// Layout
import { SeriesColorEnum } from '../../../../../layout/elements/LineChart';

/* **************************************** ENUMS ********************************************** */

export enum SensorCategory {
  motor = 'motor',
  lube = 'lube',
  vibration = 'vibration',
}

/* *************************************** INTERFACES ******************************************* */

export interface SensorConfig {
  category: SensorCategory;
  decimals: number;
  enableFlag?: keyof EquipmentUnitDataVSICrusher;
  maxScale: number;
  measurementType?: MeasurementType;
  minScale: number;
  nameKey: string;
  unit: string;
}

/* **************************************** TYPES ********************************************** */

export type SensorTag =
  | 'crusherMotor1Amps'
  | 'crusherMotor2Amps'
  | 'crusherMotorRPM'
  | 'lubricationReturnTemperature'
  | 'lubricationTankTemperature'
  | 'lubricationSupplyTemperature'
  | 'vibrationSensor1';

/* **************************************** CONSTANTS ******************************************* */

export const MAX_SELECTED_TAGS: number = 6;
export const MAX_Y_AXES: number = 4;

// Positional color palette: index 0 = 1st selected sensor, index 1 = 2nd, etc.
// Colors are visually distinct to guarantee no repetition among the 6 slots.
export const SERIES_COLOR_PALETTE: SeriesColorEnum[] = [
  SeriesColorEnum.blue,
  SeriesColorEnum.orange,
  SeriesColorEnum.pink,
  SeriesColorEnum.lightBlue,
  SeriesColorEnum.green,
  SeriesColorEnum.yellow,
];

export const SENSOR_CATEGORY_ORDER: SensorCategory[] = [
  SensorCategory.motor,
  SensorCategory.lube,
  SensorCategory.vibration,
];

// Keyed by SensorTag for completeness, but exported as Record<string, ...>
// so consumers can index with dynamic strings from selectedTags.
export const SENSOR_CONFIG: { [K in SensorTag]: SensorConfig } & Record<string, SensorConfig> = {
  crusherMotor1Amps: {
    category: SensorCategory.motor,
    decimals: 0,
    maxScale: 750,
    minScale: 0,
    nameKey: 'view.crusher.trendings.vsi.custom.sensors.crusherMotor1Amps',
    unit: 'A',
  },
  crusherMotor2Amps: {
    category: SensorCategory.motor,
    decimals: 0,
    enableFlag: 'crusherMotorQty',
    maxScale: 750,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.crusherMotor2Amps',
    unit: 'A',
  },
  crusherMotorRPM: {
    category: SensorCategory.motor,
    decimals: 0,
    enableFlag: 'crusherSpeedMonitorEnable',
    maxScale: 500,
    minScale: 0,
    nameKey: 'view.crusher.trendings.vsi.custom.sensors.crusherMotorRPM',
    unit: 'RPM',
  },
  lubricationReturnTemperature: {
    category: SensorCategory.lube,
    decimals: 0,
    maxScale: 60,
    measurementType: MeasurementType.temperature,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.lubricationReturnTemperature',
    unit: '°C',
  },
  lubricationTankTemperature: {
    category: SensorCategory.lube,
    decimals: 0,
    maxScale: 60,
    measurementType: MeasurementType.temperature,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.lubricationTankTemperature',
    unit: '°C',
  },
  lubricationSupplyTemperature: {
    category: SensorCategory.lube,
    decimals: 0,
    enableFlag: 'lubeSupplyTempEnable',
    maxScale: 60,
    measurementType: MeasurementType.temperature,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.lubricationSupplyTemperature',
    unit: '°C',
  },
  vibrationSensor1: {
    category: SensorCategory.vibration,
    decimals: 0,
    enableFlag: 'vibrationSensor1Enable',
    maxScale: 100,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.vibrationSensor1',
    unit: '%',
  },
};
